/**
 * Reading a scene for a host that describes it in text: an outline of its
 * hierarchy, and the objects that match a query. Both change nothing, record
 * nothing, and read a playing scene as they read one being edited.
 *
 * Each object comes with its `path`, and its parent's, as
 * {@link resolveObjectRef} takes them. While sibling names are unique, a host
 * that prints them prints refs that name the object back. Where siblings
 * share a name (a scene loaded while playing, content inside a scene
 * reference, a raw `SceneObject.setParent`), they share a path, and the ref
 * comes back `AMBIGUOUS`. How the result reads is the host's to decide.
 * The engine root is never listed, and never a `parentPath`.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Renderer } from '../../behaviors/renderer';
import { isPropertyBinding } from '../../behaviors/tracebehavior';

/** An object as the read operations hand it out. */
export interface ObjectEntry {
    object: SceneObject;
    /** The object's path. */
    path: string;
    /** Its parent's path, or undefined when the parent is the engine root. */
    parentPath?: string;
}

export interface OutlineEntry extends ObjectEntry {
    /** Levels below the outline's top: 0 for an object at the top. */
    depth: number;
    /** How many children the object has. */
    childCount: number;
    /** How many of them the outline leaves out, for `maxDepth` or the budget. */
    omittedChildren: number;
}

export interface OutlineOptions {
    /** Outline this object's subtree. By default, the whole scene. */
    root?: SceneObject;
    /** The deepest level listed; 0 lists only the top. By default, every level. */
    maxDepth?: number;
    /**
     * The most objects listed. The budget keeps whole levels, from the top
     * down: a level is listed only if all of it fits with the levels above.
     * When the first level with more than one object doesn't fit, it is
     * listed as far as it fits, so a single root (the document root, or
     * `root`) never hides the level below it.
     */
    maxObjects?: number;
    /** The most the listed entries may measure in total. Without `measure` it has no effect. */
    maxSize?: number;
    /**
     * An entry's size in the host's own unit, such as the characters it
     * prints. It is called before `omittedChildren` is known, and can be
     * called for entries that `maxSize` then leaves out.
     */
    measure?: (entry: OutlineEntry) => number;
}

export interface SceneOutline {
    /** Each object before its children, with siblings in the scene's order. */
    entries: OutlineEntry[];
    /** How many objects the scene has. */
    total: number;
}

/**
 * The scene's hierarchy, from the objects under the engine root or from
 * `options.root`, as far down as `maxDepth` and the budget allow.
 */
export function outlineScene(scene: TraceScene, options: OutlineOptions = {}): SceneOutline {
    const objects = sceneObjects(scene);
    const members = new Set(objects);
    const children = new Map<SceneObject, SceneObject[]>();
    const top: SceneObject[] = [];
    for (const object of objects) {
        const parent = object.getParent();
        if (parent && members.has(parent)) {
            const siblings = children.get(parent);
            if (siblings) siblings.push(object);
            else children.set(parent, [object]);
        } else {
            top.push(object);
        }
    }

    const start = options.root ? (members.has(options.root) ? [options.root] : []) : top;
    const maxObjects = options.maxObjects ?? Infinity;
    const maxSize = options.measure ? options.maxSize ?? Infinity : Infinity;
    const listed = new Map<SceneObject, OutlineEntry>();
    let size = 0;
    let level = start;
    for (let depth = 0; level.length > 0 && depth <= (options.maxDepth ?? Infinity); depth++) {
        // Only levels of one object above: this level is the first that can be cut.
        const whole = listed.size > depth;
        if (whole && listed.size + level.length > maxObjects) break;
        const accepted: OutlineEntry[] = [];
        let levelSize = 0;
        for (const object of level) {
            if (listed.size + accepted.length >= maxObjects) break;
            const entry: OutlineEntry = {
                ...objectEntry(object),
                depth,
                childCount: children.get(object)?.length ?? 0,
                omittedChildren: 0,
            };
            const entrySize = options.measure?.(entry) ?? 0;
            if (size + levelSize + entrySize > maxSize) break;
            accepted.push(entry);
            levelSize += entrySize;
        }
        if (whole && accepted.length < level.length) break;
        for (const entry of accepted) listed.set(entry.object, entry);
        size += levelSize;
        if (accepted.length < level.length) break;
        level = accepted.flatMap(entry => children.get(entry.object) ?? []);
    }

    const entries: OutlineEntry[] = [];
    const visit = (object: SceneObject): void => {
        const entry = listed.get(object);
        if (!entry) return;
        entries.push(entry);
        const own = children.get(object) ?? [];
        entry.omittedChildren = own.filter(child => !listed.has(child)).length;
        own.forEach(visit);
    };
    start.forEach(visit);
    return { entries, total: objects.length };
}

/** What {@link queryObjects} matches. Each field given must hold. */
export interface ObjectQuery {
    /**
     * The object's name, ignoring case: a glob over the whole name when it
     * has a `*`, else a part of it.
     */
    name?: string;
    /** The `@type` of a behavior the object's data has. */
    behaviorType?: string;
    /** `#RRGGBB` or `#RRGGBBAA`: a color one of its material slots is set to. */
    color?: string;
    active?: boolean;
    /**
     * Its parent's name, exactly: every child of a parent of that name.
     * The engine root's name matches the objects at the top.
     */
    parentName?: string;
    /** Whether it has a source, such as a primitive or a model. */
    hasSrc?: boolean;
}

/** A material slot's base color, set as a value rather than bound to a property. */
export interface SlotColor {
    /** The slot's mesh index (`targetMaterialIndex`). */
    slot: number;
    /** The slot's material name, once its mesh has one. */
    material?: string;
    /** The color as it is written. */
    color: string;
}

export interface ObjectMatch extends ObjectEntry {
    /** Its behaviors' `@type`s, in the order of its data. */
    behaviors: string[];
    /** Its Renderer's slot colors, in the order of its data. */
    colors: SlotColor[];
}

export type QueryResult =
    | { matches: ObjectMatch[]; total: number }
    | { error: 'INVALID_COLOR'; message: string };

/**
 * The objects that match `query`, in the scene's order, up to `limit`.
 * `total` is how many objects the scene has.
 */
export function queryObjects(
    scene: TraceScene,
    query: ObjectQuery = {},
    options: { limit?: number } = {},
): QueryResult {
    const color = query.color === undefined ? undefined : normalizeColor(query.color);
    if (color === null) {
        return { error: 'INVALID_COLOR', message: `"${query.color}" is not a color. Use #RRGGBB or #RRGGBBAA.` };
    }
    const name = query.name === undefined ? undefined : namePattern(query.name);
    const limit = options.limit ?? Infinity;
    const objects = sceneObjects(scene);
    const matches: ObjectMatch[] = [];
    for (const object of objects) {
        if (matches.length >= limit) break;
        if (name && !name.test(object.name)) continue;
        const behaviors = (object._getBehaviorData() ?? []).map(behavior => behavior['@type']);
        if (query.behaviorType !== undefined && !behaviors.includes(query.behaviorType)) continue;
        if (query.active !== undefined && object.active !== query.active) continue;
        if (query.parentName !== undefined && object.getParent()?.name !== query.parentName) continue;
        if (query.hasSrc !== undefined && query.hasSrc !== (object.src.length > 0)) continue;
        const colors = slotColors(object);
        if (color && !colors.some(slot => normalizeColor(slot.color) === color)) continue;
        matches.push({ ...objectEntry(object), behaviors, colors });
    }
    return { matches, total: objects.length };
}

function sceneObjects(scene: TraceScene): SceneObject[] {
    const engineRoot = scene.getSceneRoot();
    return scene.getSceneObjects().filter(object => object !== engineRoot);
}

function objectEntry(object: SceneObject): ObjectEntry {
    const parent = object.getParent();
    const entry: ObjectEntry = { object, path: object.getPath() };
    if (parent && parent !== object.traceScene.getSceneRoot()) entry.parentPath = parent.getPath();
    return entry;
}

function namePattern(pattern: string): RegExp {
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    return pattern.includes('*') ? new RegExp(`^${escaped.replace(/\*/g, '.*')}$`, 'i') : new RegExp(escaped, 'i');
}

/** `#RRGGBBAA` in upper case, or null for anything that isn't a color. */
function normalizeColor(color: string): string | null {
    const upper = color.trim().toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(upper)) return `${upper}FF`;
    return /^#[0-9A-F]{8}$/.test(upper) ? upper : null;
}

function slotColors(object: SceneObject): SlotColor[] {
    const renderer = object.getBehavior(Renderer);
    const materials = renderer?.getData().materials;
    if (!renderer || !materials) return [];
    const names = new Map<number, string>();
    for (const [slot, mesh] of renderer.getMeshSlots()) {
        if (mesh.material?.name) names.set(slot, mesh.material.name);
    }
    for (const [slot, materialName] of Object.entries(renderer.getOriginalMaterials())) {
        if (materialName && !names.has(Number(slot))) names.set(Number(slot), materialName);
    }
    const colors: SlotColor[] = [];
    for (const material of materials) {
        const color = material.baseColorFactor;
        // A slot bound to a property (`$tint`, `{tint}`, `#ff00{alpha}`) has no color of its own to report.
        if (typeof color !== 'string' || isPropertyBinding(color)) continue;
        const slot = material.targetMaterialIndex ?? 0;
        colors.push({ slot, material: names.get(slot), color });
    }
    return colors;
}
